/* eslint-disable max-lines */
import track from 'src/lib/Analytics';
import { toKebabCase } from 'src/lib/Utils/helpers';
import { assign, createMachine } from 'xstate';
import {
  FindContractorType,
  ServiceCardItemType,
  ServiceCardsDataItemType,
} from '../api/jsonTypes';
import { InfoLinkPropsType } from './components/ContractorCard/components/InfoLink';
import {
  ContactFormInput,
  GenerateReferralsPayloadType,
  TrackResultsLoadedDataType,
  TrackStepLoadedDataType,
} from './types';

export interface Context {
  activeStepIndex: number;
  stepOneAnswer: ServiceCardsDataItemType | null;
  stepTwoAnswer: ServiceCardItemType;
  stepThreeAnswer: ContactFormInput;
  stepFourAnswer: string;
  stepFiveAnswer: string;
  stepSixAnswer: string;
  apiUrl: string;
  apiResponse: any;
  directionAnimating: '' | 'forward' | 'back';
  stepHeaders: FindContractorType['stepHeaders'];
  referralSource: 'offers' | 'rebates' | 'decom' | 'na';
  resultsTitle: string;
  noResultsTitle: string;
  analyticsOfferCode: string;
  isGenerateReferralsButtonDisabled: boolean;
}

const initialContext = {
  activeStepIndex: 0,
  stepHeaders: [],
  referralSource: 'na',
  stepOneAnswer: null,
  stepTwoAnswer: { img: '', apiName: '', header: '' },
  stepThreeAnswer: {
    firstName: '',
    lastName: '',
    streetAddress1: '',
    streetAddress2: '',
    stateCode: '',
    city: '',
    zip: '',
    phone: '',
    email: '',
    offerCode: '',
  },
  stepFourAnswer: '',
  stepFiveAnswer: '',
  stepSixAnswer: '',
  directionAnimating: '',
  resultsTitle: '',
  noResultsTitle: '',
  apiUrl: '',
  apiResponse: null,
  analyticsOfferCode: 'na',
  isGenerateReferralsButtonDisabled: false,
};

const generateReferrals = async (payload: GenerateReferralsPayloadType, endpoint: string) => {
  let response;
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        accept: 'text/plain',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw new Error('Error generating referrals');
  }

  if (!response.ok) {
    throw new Error('Bad response generating referrals');
  }

  const data = await response.json();
  return data;
};

export const formatVpvString = (s: string) => {
  return toKebabCase(
    s.replace(/\./g, '').replace(/\?/g, '').replace(/'/g, '').replace(/&/g, 'and').replace(/!/g, '')
  );
};

const trackStepLoaded = ({
  stepName,
  stepHeader,
  referralSource,
  appendedText,
}: TrackStepLoadedDataType) => {
  track({
    event: 'send-VPV',
    'vpv-name': formatVpvString(
      `/vpv/fid/page/find-contractor/load/find-contractor/${referralSource}/${stepName || 'na'}/${
        stepHeader || 'na'
      }${appendedText ? '/' + appendedText : ''}`
    ),
  });
};

const trackResultsLoaded = ({
  resultsCount,
  referralSource,
  resultsTitle,
  modalTitle,
  category,
  service,
  jurisdiction,
  offerCode,
  zip,
  stepFourAnswer,
  stepFiveAnswer,
}: TrackResultsLoadedDataType) => {
  if (resultsCount === 0) {
    track({
      event: 'send-VPV',
      'vpv-name': formatVpvString(
        `/vpv/fid/modal/find-contractor/load/find-contractor/${referralSource}/na/${modalTitle}/${category}/${service}/${jurisdiction}/na/na/${offerCode}/0/${zip}`
      ),
    });
    track.component({
      event: 'event-click',
      category: 'fid-no-contractors-found',
      action: `${zip}`,
      label: `${category}-|-${service}`,
    });
  } else if (resultsCount > 0) {
    track({
      event: 'send-VPV',
      'vpv-name': formatVpvString(
        `/vpv/fid/page/find-contractor/load/find-contractor/${referralSource}/na/${resultsTitle}/${category}/${service}/${jurisdiction}/${stepFourAnswer
          .replace(' / ', '-')
          .replace('/', '-')}/${stepFiveAnswer}/${offerCode}/${resultsCount}`
      ),
    });
  }
};

export const findContractorMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QDMCWA7CBhA9ugLgE4CGAxvjoQMQDKAKgPIAKA+gIIByAkgLIDaABgC6iUAAccsVPlR5RIAB6IAHKoB0AFgBsygOxaArLoDMAgIzHdGgDQgAnogCcWrWoBMBged2ODl-VoAvoG2aJi4BCTklLR0bABKdCwMAGoAovGCIkggElIycjlKCEa2DghuZq7GZrq6ZnpmfgLOGsGhGNh4RGQU1PQJSWxJ9GlMxlnyedKy6PLFpfaIusoCmhqWWhqOxo5uAgZu7SBhXZG9lGqw+GBiDOhgVFgMHHRcHACqaZM50wVzRUQRjMagEuwEujcbg0ykcDQMZUQ+wMaiaWg8xhqyi2jg0RxCJ06ER60UIVxuYjoAHccE8Xm9Pt9hFNJDNCqAFsoQQI3MYNBorDozG4toiSgJXOjPC0NGYDuZjMdTsSon1ybdqbSmPE0ikuAwPjQfuJWf95kCVmofDplNszHs8WKuWppV4BNjISKqkqid1VZdrhqABaEMCPZ6vd5fY25U2zc0leqg8GemFw5QIpYIFbctG7RwQjMHH3hP0XMmBykhsNUbW6-WGmN-eOAxPclNQtPwsXGAwo2ESmrGLZGPy6EtnElqysAMRwAFdqBGGdHmb84+zFIg5ZC1DtdAZ7eZapZM+UVsYXXnHAWVp4DBOVeX1WI54vazq9QajWuTfkWxyFrKFaziqHaDpuGKhi6GovY7HoGgGFsbhWI+Zaki+M6oAAbuG9JRky2R-myAKAQgO4or2TRckhArGLaYqeI4qIeF4uJyp4FhoecGGzjhjx1l+ja-rG-6bsUO4aO4YJmPyYK+NCNhZoYzEZvBVi0ShbQEsq6HThSNCoAotAfAAQjwXB0E2G6kVu5EQm4e6nkeZgngeYpemoXKsYhEKGDC3FTgGBlGR+9bftZYm2RJDlOQeLluWeiC9sB4JaEOI4HpYgX+mSyBgPgpBBhgUDxGA+WECQAA2sBUBAeBgGoGDYTgADWjW6Txar5YVxXoKV5VgJVxA1QgzU4KQxD-FkkUkQmTR4rByhuLaEpynsIoeRCoK8vyco6JUWg7Dlz49UVJVlRV1W1UNhCXGIVVTcglAALZqJ1QV5QV539Zdt0jbAY3oC1k3TcIs1mq2C2OfRK0aGtLRQloPZeGoWjmEhd57J6J0YaGsDzlV+C1VgnBYGkAAyLA8AwAAibAUxDAF2dRqkrQtTQeLCYquWs-J+LJlTCs4w7BAS6A4BAcDyB9uUslFCaOGKAC0KI3urGsaz4uP6bc9xgPLc2titME0XK2IZr26IeVUsFIasTSeNClQPjpvpdcFGo0obkNkT4awNPFlhwnC-I9ruvg5nsWjCniTQ657Vahgb64K8bzorAWLiqOlejGGKKx88ty1WG4BYZviHSlh7FYUm+hA+8zxT+6ieiHsH9qyUp564laVT9+YKESkEbvV59mH8Y34nbitKIGPukJwpUji6FBhheW3wp9gYtooQnte3IZxTEb7dk+Mx2gtIe2x9hYSUlK5oJ7PaR01CY8-aVXk65e9319QNV0AZT2ijPDMLoF5l2FPaVeWZPIZjREdFefhXZfyfHjOAhNibAPmnUVw+wahGCOn2OEMDyhQkvAKEhBxs4r0roSMeP9bqUGwVDXB0kCH6F8PPWoHkbxowxloFYIpMTOFdsEIAA */
    id: 'findContractor',
    initial: 'stepOne',
    tsTypes: {} as import('./machine.typegen').Typegen0,
    predictableActionArguments: true,
    schema: {
      events: {} as
        | { type: 'CONTINUE'; data?: any }
        | { type: 'PREVIOUS'; data?: any }
        | { type: 'SUBMIT'; data: string }
        | { type: 'CANCEL_MODAL' }
        | { type: 'START_OVER' }
        | { type: 'STOP_ANIM' }
        | {
            type: 'START_AT_STEP3';
            data: { stepOneAnswer: any; stepTwoAnswer: any; stepThreeAnswer?: any };
          }
        | {
            type: 'TRACK_CONTRACTOR_CARD_CLICK';
            data: InfoLinkPropsType['linkType'];
          }
        | {
            type: 'TRACK_REBATE_MODAL_LOAD';
            data: { modalTitle: string; analyticsOfferCode: string };
          },
      context: {} as Context,
    },
    context: initialContext as Context,
    on: {
      STOP_ANIM: { actions: 'stopAnimation' },
      START_OVER: { target: 'stepOne', actions: 'resetContext' },
      START_AT_STEP3: { target: 'stepThree', actions: 'saveStepThreeInitialData' },
    },
    states: {
      stepOne: {
        entry: 'trackStepOneLoaded',
        on: {
          CONTINUE: {
            target: 'stepTwo',
            actions: ['incrementStepIndex', 'animateForward', 'saveStepOneAnswer'],
          },
        },
      },
      stepTwo: {
        entry: 'trackStepTwoLoaded',
        on: {
          CONTINUE: {
            target: 'stepThree',
            actions: ['saveStepTwoAnswer', 'incrementStepIndex', 'animateForward'],
          },
          PREVIOUS: {
            target: 'stepOne',
            actions: ['decrementStepIndex', 'animateBack'],
          },
        },
      },
      stepThree: {
        entry: 'trackStepThreeLoaded',
        on: {
          CONTINUE: {
            target: 'stepFour',
            actions: ['saveStepThreeAnswer', 'incrementStepIndex', 'animateForward'],
          },
          PREVIOUS: {
            target: 'stepTwo',
            actions: ['saveStepThreeAnswer', 'decrementStepIndex', 'animateBack'],
          },
        },
      },
      stepFour: {
        entry: 'trackStepFourLoaded',
        on: {
          CONTINUE: {
            target: 'stepFive',
            actions: ['saveStepFourAnswer', 'incrementStepIndex', 'animateForward'],
          },
          PREVIOUS: {
            target: 'stepThree',
            actions: ['decrementStepIndex', 'animateBack'],
          },
        },
      },
      stepFive: {
        entry: 'trackStepFiveLoaded',
        on: {
          CONTINUE: {
            target: 'stepSix',
            actions: [
              'saveStepFiveAnswer',
              'incrementStepIndex',
              'animateForward',
              'enableGenerateReferralsButton',
            ],
          },
          PREVIOUS: {
            target: 'stepFour',
            actions: ['decrementStepIndex', 'animateBack'],
          },
        },
      },
      stepSix: {
        entry: 'trackStepSixLoaded',
        on: {
          SUBMIT: {
            target: 'fetchingReferrals',
            actions: 'saveStepSixAnswer',
          },
          PREVIOUS: {
            target: 'stepFive',
            actions: ['saveStepSixAnswer', 'decrementStepIndex', 'animateBack'],
          },
          TRACK_REBATE_MODAL_LOAD: {
            actions: ['trackRebateFoundModalLoaded', 'saveAnalyticsOfferCode'],
          },
        },
      },
      fetchingReferrals: {
        invoke: {
          src: 'fetchContractorData',
          onDone: {
            target: 'results',
            actions: 'assignContractorResults',
          },
          onError: {
            target: 'error',
          },
        },
      },
      results: {
        entry: 'trackResultsLoaded',
        on: {
          CANCEL_MODAL: {
            target: 'stepSix',
            actions: ['resetApiResponse', 'disableGenerateReferralsButton'],
          },
          TRACK_CONTRACTOR_CARD_CLICK: {
            actions: 'trackContractorCardLinkClick',
          },
        },
      },
      error: {},
    },
  },
  {
    services: {
      fetchContractorData: async context => {
        const {
          firstName,
          lastName,
          email,
          phone,
          streetAddress1,
          streetAddress2,
          city,
          stateCode,
          zip,
          offerCode,
        } = context.stepThreeAnswer;
        const payload = {
          attributeId_ProjectType: 41,
          attributeId_Location: context.stepFourAnswer.toLowerCase().includes('business') ? 47 : 46,
          attributeId_ProjectStatus: 39,
          attributeId_ProjectTimeline: 44,
          customerAddress: streetAddress2 ? `${streetAddress1}, ${streetAddress2}` : streetAddress1,
          customerCity: city,
          customerEmailAddress: email,
          customerFirstName: firstName,
          customerLastName: lastName,
          customerPhoneNumber: phone,
          customerStateCode: stateCode,
          customerZipCode: zip,
          specialOfferCode: offerCode,
          isDukeEnergyCustomer: context.stepFiveAnswer.toLowerCase().includes('yes'),
          // offers come with service *category* set as fidService query param instead of service, so this will be '' if offerCode set
          // primaryServiceName, attributeId_Location, and projectDescription all labeled "Required if referral is NOT for a special offer" in the api docs
          primaryServiceName: context.stepTwoAnswer.apiName,
          projectDescription: context.stepSixAnswer,
        };

        const data = await generateReferrals(payload, context.apiUrl);

        return data;
      },
    },
    actions: {
      trackStepOneLoaded(context) {
        const { stepHeaders, referralSource } = context;
        const { name, header } = stepHeaders[0];
        const data = {
          stepName: name,
          stepHeader: header,
          referralSource,
        };
        trackStepLoaded(data);
      },
      trackStepTwoLoaded(context) {
        const { stepHeaders, referralSource, stepOneAnswer } = context;
        const { name, header } = stepHeaders[1];
        const data = {
          stepName: name,
          stepHeader: header,
          referralSource,
          stepOneAnswer,
          appendedText: stepOneAnswer?.categoryHeader?.replace('/', '-') || 'na',
        };
        trackStepLoaded(data);
      },
      trackStepThreeLoaded(context) {
        const { stepHeaders, referralSource, stepOneAnswer, stepTwoAnswer } = context;
        const { name, header } = stepHeaders[2];
        const data = {
          stepName: name,
          stepHeader: header,
          referralSource,
          appendedText: `${stepOneAnswer?.categoryHeader?.replace('/', '-') || 'na'}/${
            stepTwoAnswer?.header || 'na'
          }`,
        };
        trackStepLoaded(data);
      },
      trackStepFourLoaded(context) {
        const { stepHeaders, referralSource, stepOneAnswer, stepTwoAnswer, stepThreeAnswer } =
          context;
        const { name, header } = stepHeaders[3];
        const data = {
          stepName: name,
          stepHeader: header,
          referralSource,
          appendedText: `${stepOneAnswer?.categoryHeader?.replace('/', '-') || 'na'}/${
            stepTwoAnswer?.header || 'na'
          }/${stepThreeAnswer?.stateCode || 'na'}`,
        };
        trackStepLoaded(data);
      },
      trackStepFiveLoaded(context) {
        const {
          stepHeaders,
          referralSource,
          stepOneAnswer,
          stepThreeAnswer,
          stepTwoAnswer,
          stepFourAnswer,
        } = context;
        const { name, header } = stepHeaders[4];
        const data = {
          stepName: name,
          stepHeader: header,
          referralSource,
          appendedText: `${stepOneAnswer?.categoryHeader?.replace('/', '-') || 'na'}/${
            stepTwoAnswer?.header || 'na'
          }/${stepThreeAnswer?.stateCode || 'na'}/${stepFourAnswer
            .replace(' / ', '-')
            .replace('/', '-')}`,
        };
        trackStepLoaded(data);
      },
      trackStepSixLoaded(context) {
        const {
          stepHeaders,
          referralSource,
          stepOneAnswer,
          stepTwoAnswer,
          stepThreeAnswer,
          stepFourAnswer,
          stepFiveAnswer,
        } = context;
        const { name, header } = stepHeaders[5];
        const data = {
          stepName: name,
          stepHeader: header,
          referralSource,
          appendedText: `${stepOneAnswer?.categoryHeader?.replace('/', '-') || 'na'}/${
            stepTwoAnswer?.header || 'na'
          }/${stepThreeAnswer?.stateCode || 'na'}/${stepFourAnswer
            .replace(' / ', '-')
            .replace('/', '-')}/${stepFiveAnswer}`,
        };
        trackStepLoaded(data);
      },
      trackResultsLoaded(context) {
        const {
          referralSource,
          stepOneAnswer,
          stepTwoAnswer,
          stepThreeAnswer,
          analyticsOfferCode,
          stepFourAnswer,
          stepFiveAnswer,
          apiResponse,
          noResultsTitle,
          resultsTitle,
        } = context;
        const data = {
          resultsCount: apiResponse.tradeAllyList?.length || 0,
          referralSource,
          jurisdiction: stepThreeAnswer?.stateCode || 'na',
          resultsTitle: resultsTitle || 'na',
          modalTitle: noResultsTitle || 'na',
          category: stepOneAnswer?.categoryHeader?.replace('/', '-') || 'na',
          service: stepTwoAnswer?.header || 'na',
          offerCode: analyticsOfferCode,
          zip: stepThreeAnswer?.zip || 'na',
          stepFourAnswer: stepFourAnswer || 'na',
          stepFiveAnswer: stepFiveAnswer || 'na',
        };
        trackResultsLoaded(data);
      },
      trackContractorCardLinkClick(context, { data }) {
        const {
          referralSource,
          resultsTitle,
          stepOneAnswer,
          stepTwoAnswer,
          stepThreeAnswer,
          analyticsOfferCode,
          stepFourAnswer,
          stepFiveAnswer,
        } = context;
        track({
          event: 'send-VPV',
          'vpv-name': formatVpvString(
            `/vpv/fid/page/find-contractor/load/find-contractor/${referralSource}/na/${
              resultsTitle || 'na'
            }/${stepOneAnswer?.categoryHeader?.replace('/', '-') || 'na'}/${
              stepTwoAnswer?.header || 'na'
            }/${stepThreeAnswer?.stateCode || 'na'}/${
              stepFourAnswer?.replace(' / ', '-').replace('/', '-') || 'na'
            }/${stepFiveAnswer || 'na'}/${analyticsOfferCode}/${data || 'na'}`
          ),
        });
      },
      trackRebateFoundModalLoaded(context, { data }) {
        const { modalTitle, analyticsOfferCode } = data;
        const {
          referralSource,
          stepOneAnswer,
          stepTwoAnswer,
          stepThreeAnswer,
          stepFourAnswer,
          stepFiveAnswer,
        } = context;
        track({
          event: 'send-VPV',
          'vpv-name': formatVpvString(
            `/vpv/fid/modal/find-contractor/load/find-contractor/${referralSource}/na/${
              modalTitle || 'na'
            }/${stepOneAnswer?.categoryHeader?.replace('/', '-') || 'na'}/${
              stepTwoAnswer?.header || 'na'
            }/${stepThreeAnswer?.stateCode || 'na'}/${
              stepFourAnswer?.replace(' / ', '-').replace('/', '-') || 'na'
            }/${stepFiveAnswer || 'na'}/${analyticsOfferCode}`
          ),
        });
      },
      saveStepOneAnswer: assign({
        stepOneAnswer: (context, { data }) => data,
      }),
      saveStepTwoAnswer: assign({
        stepTwoAnswer: (context, { data }) => data,
      }),
      saveStepThreeAnswer: assign({
        stepThreeAnswer: (context, { data }) => data,
      }),
      saveStepFourAnswer: assign({
        stepFourAnswer: (context, { data }) => data,
      }),
      saveStepFiveAnswer: assign({
        stepFiveAnswer: (context, { data }) => data,
      }),
      saveStepSixAnswer: assign({
        stepSixAnswer: (context, { data }) => data,
      }),
      enableGenerateReferralsButton: assign({
        isGenerateReferralsButtonDisabled: false,
      }),
      disableGenerateReferralsButton: assign({
        isGenerateReferralsButtonDisabled: true,
      }),
      incrementStepIndex: assign({
        activeStepIndex: context => context.activeStepIndex + 1,
      }),
      decrementStepIndex: assign({
        activeStepIndex: context => context.activeStepIndex - 1,
      }),
      assignContractorResults: assign((_, { data }) => {
        return {
          apiResponse: data,
        };
      }),
      saveAnalyticsOfferCode: assign((_, { data }) => {
        return { analyticsOfferCode: data.analyticsOfferCode };
      }),
      stopAnimation: assign({ directionAnimating: '' }),
      animateForward: assign({ directionAnimating: 'forward' }),
      animateBack: assign({ directionAnimating: 'back' }),
      saveStepThreeInitialData: assign({
        activeStepIndex: 2,
        stepOneAnswer: (context, { data }) => {
          return data.stepOneAnswer;
        },
        stepTwoAnswer: (context, { data }) => {
          return data.stepTwoAnswer;
        },
        stepThreeAnswer: (context, { data }) => {
          return {
            ...(initialContext.stepThreeAnswer as ContactFormInput),
            offerCode: data.stepThreeAnswer.offerCode,
          };
        },
      }),
      resetContext: assign({
        ...(initialContext as Context),
        apiUrl: context => context.apiUrl,
        stepHeaders: context => context.stepHeaders,
        resultsTitle: context => context.resultsTitle,
        noResultsTitle: context => context.noResultsTitle,
        referralSource: context => context.referralSource,
      }),
      resetApiResponse: assign({ apiResponse: null }),
    },
  }
);
